/**
 * Reusable read-only input field component
 * @module FormFields/ReadOnlyInputField
 */

import { memo } from 'react';
import lockIcon from '../../../../public/assets/images/lockSolid.svg';

/**
 * Props for ReadOnlyInputField component
 */
export interface ReadOnlyInputFieldProps {
  /** Field label */
  label: string;
  /** Field value to display */
  value: string;
  /** Unique ID for the field */
  id: string;
  /** Optional helper text */
  helperText?: string;
}

/**
 * Read-only text input field with lock icon for auto-populated values
 * Used for input fields that are populated by the system and cannot be edited by users
 * (e.g., NUID, First Name, Last Name fields populated from HR systems)
 * 
 * @param props - Component props
 * @param props.label - Field label
 * @param props.value - Field value to display
 * @param props.id - Unique ID for the field
 * @param props.helperText - Optional helper text to explain why field is read-only
 * @returns ReadOnlyInputField component
 */
export const ReadOnlyInputField = memo<ReadOnlyInputFieldProps>(({ 
  label, 
  value, 
  id, 
  helperText
}) => (
  <div
    className="ds-form w-full"
    data-ds-theme="vessel"
    data-ds-variant="input"
    data-ds-version="1"
  >
    <label htmlFor={id}>{label}</label>
    <div className="w-full relative">
      <input
        autoComplete="on"
        className="w-full min-w-0 block ds-form__disabled-field bg-gray-100 text-gray-600 pr-10"
        style={{ width: "100%" }}
        aria-describedby={helperText ? `${id}-help` : undefined}
        id={id}
        name={id}
        type="text"
        value={value}
        disabled
      />
      <img
        src={lockIcon}
        alt=""
        aria-hidden="true"
        className="absolute right-3 top-3"
      />
    </div>
    {helperText && (
      <div className="helper-text text-sm text-gray-500" id={`${id}-help`}>
        {helperText}
      </div>
    )}
  </div>
));

ReadOnlyInputField.displayName = 'ReadOnlyInputField';
